import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, ExternalLink, Zap, UserPlus, ListPlus } from 'lucide-react';
import type { OrganizationWithPeople } from '../../types';
import { CustomerStatusBadge } from './CustomerStatusBadge';
import { FavoriteStar, AddToListSheet } from '../lists';
import { DirectionsButton } from '../directions';
import { orgTypeToListKind, orgTypeLabel } from '../../lib/orgTypes';

const BLAST_APP_URL = import.meta.env.VITE_BLAST_APP_URL || 'https://dev.blastathletics.com';

interface OrganizationHeaderProps {
  organization: OrganizationWithPeople;
  onAddPerson?: () => void;
  /**
   * Gates inline mutation affordances (currently the Add Person button).
   * Defaults to true so existing callsites that don't yet pass it are unaffected.
   */
  canEdit?: boolean;
}

/**
 * Organization detail page header
 * Shows name, location, badges, and back button
 */
export const OrganizationHeader = ({ organization, onAddPerson, canEdit = true }: OrganizationHeaderProps) => {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link
        to="/organizations"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Organizations
      </Link>

      {/* Header card */}
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            {/* Icon */}
            <div className="p-2.5 sm:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
              <Building2 className="h-7 w-7 sm:h-8 sm:w-8 text-purple-600 dark:text-purple-400" />
            </div>

            {/* Name and location */}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 leading-tight">
                <span className="break-words">{organization.name}</span>
                {organization.blastSchoolId && (
                  <span title="Source: Blast" className="inline-flex flex-shrink-0">
                    <Zap className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {[organization.city, organization.state]
                  .filter(Boolean)
                  .join(', ') || 'Location not specified'}
              </p>

              {/* Badges */}
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {organization.type === 'SCHOOL' ? (
                  <>
                    {/* Public/Private badge */}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.7rem] font-medium tracking-wide ${
                        organization.isPrivate
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}
                    >
                      {organization.isPrivate ? 'Private School' : 'Public School'}
                    </span>

                    {/* Level badge */}
                    {organization.ncesLevel && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.7rem] font-medium tracking-wide bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                        {organization.ncesLevel}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.7rem] font-medium tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                    {orgTypeLabel(organization.type)}
                  </span>
                )}

                {/* Customer status badge */}
                <CustomerStatusBadge status={organization.customerStatus} size="md" />

                {organization.type === 'SCHOOL' && organization.ncesIsCharter && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.7rem] font-medium tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                    Charter School
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
            <FavoriteStar kind={orgTypeToListKind(organization.type)} id={organization.id} size={20} />
            <DirectionsButton destination={organization} />
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="btn-secondary text-sm justify-center flex-1 sm:flex-initial whitespace-nowrap"
            >
              <ListPlus className="h-4 w-4 mr-1" />
              Add to list
            </button>
            {canEdit && onAddPerson && (
              <button
                type="button"
                onClick={onAddPerson}
                className="btn-primary text-sm justify-center flex-1 sm:flex-initial whitespace-nowrap"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Add Person
              </button>
            )}
            {organization.blastSchoolId && (
              <a
                href={`${BLAST_APP_URL}/schools/${organization.blastSchoolId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors flex-1 sm:flex-initial whitespace-nowrap"
              >
                <Zap className="h-4 w-4 flex-shrink-0" />
                View in Blast
              </a>
            )}
            {organization.website && (
              <a
                href={
                  organization.website.startsWith('http')
                    ? organization.website
                    : `https://${organization.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm justify-center flex-1 sm:flex-initial whitespace-nowrap"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Website
              </a>
            )}
          </div>
        </div>
      </div>

      <AddToListSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kind={orgTypeToListKind(organization.type)}
        id={organization.id}
        recordLabel={organization.name}
      />
    </div>
  );
};
